import React, { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { formatCurrency, formatDate, formatRelativeDate } from '@/lib/utils'
import { contactSnapshot } from '@/lib/contactSnapshot'
import { Plus, Search, X, FileText, DollarSign, Clock, CheckCircle2 } from 'lucide-react'
import { toast } from 'sonner'
import { getQuoteStatusConfig, QUOTE_STATUS_FILTER_OPTIONS } from './quote-status'

function NewQuoteModal({ onClose, onCreated, contacts }: any) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({
    contact_id: '', contact_name: '', company_id: '', company_name: '', contact_email: '',
    title: '', valid_until: '', notes: '', service_line_id: '',
  })

  // Active service lines for the picker — same query shape as
  // QuoteDetail. Single-modal lifetime so no extra cache concerns.
  const { data: serviceLines = [] } = useQuery({
    queryKey: ['quote-modal-service-lines', workspace?.id],
    enabled: !!workspace?.id,
    queryFn: async () => {
      const { data } = await supabase.from('service_lines')
        .select('id, name')
        .eq('workspace_id', workspace!.id)
        .eq('is_active', true)
        .order('sort_order')
      return data ?? []
    },
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))
  const inp: React.CSSProperties = {
    width: '100%', padding: '9px 12px',
    background: 'var(--field-bg)',
    border: '1px solid var(--border-default)',
    borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)',
    outline: 'none', boxSizing: 'border-box',
  }

  const handleContactSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const c = contacts.find((c: any) => c.id === e.target.value)
    if (!c) return
    const snap = contactSnapshot(c)
    setForm(p => ({
      ...p,
      contact_id: c.id,
      contact_name: snap.contact_name,
      company_id: c.company_id || '',
      company_name: snap.company_name ?? '',
      contact_email: c.email || ''
    }))
  }

  const handleSave = async () => {
    if (!form.contact_name.trim()) { toast.error('Please select a contact'); return }
    setSaving(true)
    // quote_number assigned by the assign_quote_number BEFORE-INSERT trigger
    // (Phase A migration 20260618000000, race-safe per-workspace counter).
    // Omitting the key lets the trigger fire.
    const { data, error } = await supabase.from('quotes').insert({
      contact_id: form.contact_id || null,
      contact_name: form.contact_name,
      company_id: form.company_id || null,
      company_name: form.company_name || null,
      contact_email: form.contact_email,
      title: form.title || `Quote for ${form.contact_name}`,
      status: 'draft',
      line_items: [],
      subtotal: 0, tax_rate: 0, tax_amount: 0,
      total: 0, discount_amount: 0, discount_type: 'none', discount_value: 0,
      currency: 'USD',
      issue_date: new Date().toISOString().split('T')[0],
      valid_until: form.valid_until || null,
      notes: form.notes,
      workspace_id: workspace!.id,
      service_line_id: form.service_line_id || null,
    }).select().single()
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success('Quote created')
    onCreated(data.id)
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New quote</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contact *</label>
            <select onChange={handleContactSelect} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select a contact...</option>
              {contacts.map((c: any) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''} {c.company_name ? `— ${c.company_name}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Quote title</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Website redesign proposal" style={inp}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Valid until</label>
            {/* TODO Session 3: theme this — colorScheme: 'dark' pinned for native date picker icons */}
            <input type="date" value={form.valid_until} onChange={e => set('valid_until', e.target.value)} style={{ ...inp, colorScheme: 'dark' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Service line</label>
            <select value={form.service_line_id} onChange={e => set('service_line_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">— (uncategorized)</option>
              {serviceLines.map((sl: any) => <option key={sl.id} value={sl.id}>{sl.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Scope, terms, anything the client should know..." rows={2}
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>
            {saving ? 'Creating...' : 'Create quote'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default function Quotes() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showNewModal, setShowNewModal] = useState(false)

  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ['quotes', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('quotes').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'typeahead-quotes', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts').select('id, first_name, last_name, email, company_id, company_name').eq('workspace_id', workspace.id).order('first_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const metrics = useMemo(() => ({
    total: quotes.length,
    pending: quotes.filter((q: any) => ['sent', 'viewed'].includes(q.status)).length,
    approved: quotes.filter((q: any) => q.status === 'approved').length,
    value: quotes.filter((q: any) => ['sent', 'viewed', 'approved'].includes(q.status)).reduce((s: number, q: any) => s + (q.total || 0), 0),
  }), [quotes])

  const filtered = useMemo(() => {
    let list = [...quotes]
    if (statusFilter !== 'all') list = list.filter((q: any) => q.status === statusFilter)
    if (search) {
      const s = search.toLowerCase()
      list = list.filter((q: any) => q.quote_number?.toLowerCase().includes(s) || q.contact_name?.toLowerCase().includes(s) || q.title?.toLowerCase().includes(s))
    }
    return list
  }, [quotes, search, statusFilter])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>
      <div style={{ padding: '24px 28px 0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Quotes</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>{quotes.length} total</p>
          </div>
          <button onClick={() => setShowNewModal(true)} className="btn-primary">
            <Plus style={{ width: 15, height: 15 }} /> New quote
          </button>
        </div>

        {/* Metrics — auto-fit so cards reflow on narrow viewports instead
            of compressing 4-up to unreadable widths on mobile. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Open value', value: formatCurrency(metrics.value), color: 'var(--accent)', icon: DollarSign },
            { label: 'Pending', value: metrics.pending, color: 'var(--info)', icon: Clock },
            { label: 'Approved', value: metrics.approved, color: 'var(--positive)', icon: CheckCircle2 },
            { label: 'Total quotes', value: metrics.total, color: 'var(--ink-tertiary)', icon: FileText },
          ].map(m => (
            <div key={m.label} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 60, height: 60, background: `radial-gradient(circle, color-mix(in srgb, ${m.color} var(--status-tint-fill), transparent) 0%, transparent 70%)`, pointerEvents: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{m.label}</span>
                <m.icon style={{ width: 14, height: 14, color: m.color }} />
              </div>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{m.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search quotes..."
              style={{ width: '100%', padding: '9px 12px 9px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {QUOTE_STATUS_FILTER_OPTIONS.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: statusFilter === s ? 'var(--accent)' : 'var(--hover-bg)', color: statusFilter === s ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: statusFilter === s ? 'none' : '1px solid var(--border-subtle)' }}>
                {s === 'all' ? 'All' : s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 28px 28px' }}>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 14, overflow: 'hidden' }}>
          {/* Column header — desktop only. Cards on mobile are self-labeling. */}
          {!isMobile && (
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 160px 120px 100px 80px', padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface)' }}>
            {['Quote', 'Customer / Title', 'Amount', 'Valid until', 'Status', ''].map((h, i) => (
              <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
            ))}
          </div>
          )}

          {isLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 13 }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '60px 24px', textAlign: 'center' }}>
              <FileText style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>
                {search || statusFilter !== 'all' ? 'No quotes match your filters' : 'No quotes yet'}
              </p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '0 0 16px' }}>
                {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first quote to start winning business'}
              </p>
              {!search && statusFilter === 'all' && (
                <button onClick={() => setShowNewModal(true)} className="btn-primary" style={{ margin: '0 auto' }}>
                  <Plus style={{ width: 14, height: 14 }} /> New quote
                </button>
              )}
            </div>
          ) : (
            filtered.map((quote: any, i: number) => {
              const cfg = getQuoteStatusConfig(quote.status)
              const isLast = i === filtered.length - 1

              // Mobile card. Same shape as Invoices for consistency:
              // customer + amount on header (the actionable headline);
              // quote number kept as mono sub-line; title (optional) as
              // tertiary sub-line; status badge below amount; valid-until
              // as a meta line.
              if (isMobile) {
                return (
                  <motion.div key={quote.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                    onClick={() => navigate(`/app/quotes/${quote.id}`)}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 150ms ease' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quote.contact_name}</p>
                        <p style={{ fontSize: 12, color: 'var(--accent-text)', fontFamily: 'monospace', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quote.quote_number}</p>
                        {quote.title && <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quote.title}</p>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                        <p style={{ fontSize: 16, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.01em' }}>{formatCurrency(quote.total || 0)}</p>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                          {cfg.label}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {quote.valid_until && (
                        <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>
                          Valid until {formatDate(quote.valid_until, 'MMM d, yyyy')}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>
                        Created {formatRelativeDate(quote.created_at)}
                      </span>
                    </div>
                  </motion.div>
                )
              }

              // Desktop grid row — unchanged.
              return (
                <motion.div key={quote.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.02, 0.1) }}
                  onClick={() => navigate(`/app/quotes/${quote.id}`)}
                  style={{ display: 'grid', gridTemplateColumns: '140px 1fr 160px 120px 100px 80px', padding: '13px 16px', borderBottom: i < filtered.length - 1 ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer', transition: 'background 150ms ease', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-text)', fontFamily: 'monospace' }}>{quote.quote_number}</span>
                  <div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0 }}>{quote.contact_name}</p>
                    {quote.title && <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quote.title}</p>}
                  </div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', margin: 0 }}>{formatCurrency(quote.total || 0)}</p>
                  <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>
                    {quote.valid_until ? formatDate(quote.valid_until, 'MMM d, yyyy') : '—'}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                    {cfg.label}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', textAlign: 'right' }}>
                    {formatRelativeDate(quote.created_at)}
                  </span>
                </motion.div>
              )
            })
          )}
        </div>
      </div>

      <AnimatePresence>
        {showNewModal && (
          <NewQuoteModal
            onClose={() => setShowNewModal(false)}
            onCreated={(id: string) => navigate(`/app/quotes/${id}`)}
            contacts={contacts}
          />
        )}
      </AnimatePresence>
    </div>
  )
}